<template>
  <div class="qr-analytics-page" ref="pdfContent">

    <!-- ================= HEADER ================= -->

    <div class="page-header">
      <div>
        <h1>QR Code Analytics</h1>
        <p>Monitor scans, traffic sources, devices, and engagement trends.</p>
      </div>

      <div class="header-actions">
        <!-- <button class="filter-btn">Last 30 Days</button> -->
        <div class="qr-filter">
          <select v-model="selectedQrId" @change="selectQr(selectedQrId)">
            <option value="" selected>All QR</option>
            <option v-for="qr in qrList" :key="qr.user_qr_id" :value="qr.user_qr_id">{{qr.user_qr_title}}</option>
          </select>
        </div>
        <button class="primary-btn" @click="newQrNow()">
          <span class="material-symbols-outlined">qr_code_2</span>
          New QR Code
        </button>

        <!-- <button  @click="exportPDF" class="ghost-btn">
          <span class="material-symbols-outlined">download</span>
          Export Report
        </button> -->
      </div>
    </div>

    <!-- ================= KPI STRIP ================= -->

    <div class="kpi-grid">

      <div class="kpi-card">
        <span>Total Scans</span>
        <strong>
          {{ stats.total_scans }}
        </strong>
        <small class="positive">Live QR Data</small>
      </div>

      <div class="kpi-card">
        <span>Unique Visitors</span>
        <strong>{{ stats.unique_scans }}</strong>
        <small class="neutral">Live Data</small>
      </div>

      <!-- <div class="kpi-card">
        <span>Conversion Rate</span>
        <strong>{{ stats.conversion_rate }}</strong>
        <small class="neutral">Live Data</small>
      </div> -->

      <div class="kpi-card">
        <span>Today's Scans</span>
        <strong>{{ stats.today_scans }}</strong>
        <small class="neutral">Live Activity</small>
      </div>

    </div>

    <!-- ================= CHARTS ================= -->

    <div class="charts-grid">

      <div class="chart-card large">
        <div class="card-header">
          <h3>Scan Trend</h3>
        </div>

        <div class="chart-wrapper">
          <div id="scanTrendChart"></div>
        </div>
      </div>

      <div class="chart-card">
        <div class="card-header">
          <h3>Device Breakdown</h3>
        </div>

        <div class="chart-wrapper">
          <div id="deviceChart"></div>
        </div>
      </div>
      <div class="chart-card">
        <div class="card-header">
          <h3>Browser Breakdown</h3>
        </div>
        <div class="chart-wrapper">
          <div id="browserChart"></div>
        </div>
      </div>

      <div class="chart-card">
        <div class="card-header">
          <h3>OS / Source Breakdown</h3>
        </div>
        <div class="chart-wrapper">
          <div id="sourceChart"></div>
        </div>
      </div>

      <div class="chart-card">
        <div class="card-header">
          <h3>Scan Type Distribution</h3>
        </div>
        <div class="chart-wrapper">
          <div id="stypeChart"></div>
        </div>
      </div>

    </div>

    <!-- ================= BOTTOM GRID ================= -->

    <div class="bottom-grid">

      <!-- ================= QR LIST ================= -->

      <div class="table-card">

        <div class="card-header table-header">
          <div>
            <h3>QR Codes</h3>
            <p>Manage & monitor QR performance</p>
          </div>
        </div>

        <table class="compact-table">

          <thead>
            <tr>
              <th>QR Info</th>
              <th>Link</th>
              <th>Scans</th>
              <th>Status</th>
              <th class="right">Actions</th>
            </tr>
          </thead>

          <tbody>

            <tr v-if="!qrList.length">
              <td colspan="4" class="empty-cell">
                No QR Codes Created
              </td>
            </tr>

            <tr v-for="qr in qrList" :key="qr.user_qr_id" @click="selectQr(qr.user_qr_id)"
              :class="{ activeRow: selectedQrId === qr.user_qr_id }">

              <td>
                <div class="qr-cell">
                  <strong>{{ qr.user_qr_title }}</strong>
                  <span v-if="qr.cmp!=null" style="margin-left: 1rem;">{{ qr.cmp.camp_title }}</span>
                </div>
              </td>
              <td>
                <strong>{{ qr.user_qr_link }}</strong>
              </td>
              <td>
                <strong>{{ qr.scans }}</strong>
              </td>

              <td>
                <span :class="['status-badge', qr.user_qr_status]">
                  {{ qr.user_qr_status }}
                </span>
              </td>

              <td class="right actions">

                <!-- Download QR -->
                <button class="table-action-btn" @click.stop="downloadQr(qr)">
                  <span class="material-symbols-outlined">download</span>
                </button>

                <!-- Copy Link -->
                <button class="table-action-btn" @click.stop="copyQrLink(qr)">
                  <span class="material-symbols-outlined">content_copy</span>
                </button>

                <!-- Share -->
                <button class="table-action-btn" @click.stop="shareQr(qr)">
                  <span class="material-symbols-outlined">share</span>
                </button>

                <!-- Delete -->
                <button class="table-action-btn danger" @click.stop="openDeleteModal(qr)">
                  <span class="material-symbols-outlined">delete</span>
                </button>

              </td>

            </tr>

          </tbody>
        </table>
        <div class="pagination">
          <button
            class="page-btn"
            :disabled="page_no1 === 0"
            @click="changeQrPage(-1)"
          >
            Prev
          </button>

          <span class="page-info">
            {{ page_no1 + 1 }}
          </span>

          <button
            class="page-btn"
            :disabled="qrList.length === 0"
            @click="changeQrPage(1)"
          >
            Next
          </button>
        </div>
      </div>

      <!-- ================= SCAN LOGS ================= -->

      <div class="table-card">

        <div class="card-header table-header">
          <div>
            <h3>Recent Scan Activity</h3>
            <p>Live visitor interactions & QR engagement</p>
          </div>
        </div>

        <table>

          <thead>
            <tr>
              <th>Browser</th>
              <th>Details</th>
              <th>Time</th>
              <th class="right">Actions</th>
            </tr>
          </thead>

          <tbody>

            <tr v-if="!scanLogs.length">
              <td colspan="4" class="empty-cell">
                No Scan Activity
              </td>
            </tr>

            <tr v-for="log in scanLogs" :key="log.id">

              <td>
                <strong>{{ log.visitor }}</strong>
              </td>

              <td>
                <div class="log-details">
                  <span>{{ log.device }}</span>
                  <span>•</span>
                  <span>{{ log.source }}</span>
                </div>
              </td>

              <td class="muted">
                {{ log.date }}
              </td>

              <td class="right">
                <button class="table-action-btn">
                  <span class="material-symbols-outlined">visibility</span>
                </button>
              </td>

            </tr>

          </tbody>
        </table>
        <div class="pagination">
          <button
            class="page-btn"
            :disabled="page_no === 0"
            @click="changeScanPage(-1)"
          >
            Prev
          </button>

          <span class="page-info">
            {{ page_no + 1 }}
          </span>

          <button
            class="page-btn"
            :disabled="scanLogs.length === 0"
            @click="changeScanPage(1)"
          >
            Next
          </button>
        </div>
      </div>

    </div>

    <!-- ================= QR MODAL ================= -->

    <div v-if="showQRModal" class="popup-overlay" @click.self="showQRModal = false">

      <div class="popup-modal">

        <div class="popup-header">
          <h3>Create New QR Code</h3>

          <button class="popup-close" @click="showQRModal = false">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div class="popup-body">

          <div class="form-group">
            <label>QR Name</label>
            <input v-model="qrForm.title" placeholder="Store Entrance QR" />
          </div>

          <div class="form-group">
            <label>QR For</label>
            <select v-model="qrForm.parent_type">
              <option value="review">Review Page</option>
              <option value="referral">Referral Page</option>
              <option value="custom">Custom URL</option>
            </select>
          </div>

          <div class="form-group" v-if="qrForm.parent_type === 'custom'">
            <label>Custom URL</label>
            <input v-model="qrForm.link" placeholder="https://example.com" />
          </div>

          <div class="form-group" v-if="qrForm.parent_type!='custom'">
            <label>Assign Campaign</label>
            <select v-if="qrForm.parent_type==='review'" v-model="qrForm.campaign">
              <option  v-for="cmp in camps" :key="cmp.camp_id" :value="cmp.camp_id">{{ cmp.camp_title }}</option>
            </select>
             <select  v-if="qrForm.parent_type==='referral'" v-model="qrForm.campaign">
              <option v-for="cmp in camps" :key="cmp.ref_camp_id" :value="cmp.ref_camp_id">{{ cmp.ref_camp_name }}</option>
            </select>
          </div>

          <div class="form-group">
            <label>Notes</label>
            <textarea v-model="qrForm.note" placeholder="Optional notes"></textarea>
          </div>

        </div>

        <div class="popup-footer">

          <button class="ghost-btn" @click="showQRModal = false">
            Cancel
          </button>

          <button class="primary-btn" :disabled="creatingQr" @click="createQrHandler">
            <span class="material-symbols-outlined">qr_code_2</span>
            {{ creatingQr ? "Generating..." : "Generate QR Code" }}
          </button>

        </div>

      </div>
    </div>
    <!-- ================= DELETE CONFIRM MODAL ================= -->

    <div v-if="showDeleteModal" class="popup-overlay">

      <div class="popup-modal confirm">

        <div class="popup-header">
          <h3>Delete QR Code</h3>

          <button class="popup-close" @click="showDeleteModal = false">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div class="popup-body">
          <p class="confirm-text">
            Are you sure you want to delete this QR code?
          </p>
          <small>This action cannot be undone.</small>
        </div>

        <div class="popup-footer">

          <button class="ghost-btn" @click="showDeleteModal = false">
            Cancel
          </button>

          <button class="danger-btn" :disabled="deletingQr" @click="confirmDeleteQr">
            {{ deletingQr ? "Deleting..." : "Delete QR" }}
          </button>

        </div>

      </div>
    </div>
  </div>
</template>
<script>
  import QRCode from "qrcode";
  import ApexCharts from "apexcharts";
  import { useToast } from 'vue-toastification';
  import html2canvas from "html2canvas";
  import jsPDF from "jspdf";
  import {
    getQrUrl
  } from "../apis/qrapis";

   import {
    mainBaseUrl,apiBaseUrl
  } from "../apis/appconst";

  import {
    getQrList,
    deleteQr,
    createQr,
    getQrKpiStats,
    getScanTrend,
    getDeviceBreakdown,
    getRecentScans
  } from "@/apis/qrapis";
  import {
  getReferralCampaigns
} from "../apis/referralapis";

  import {
    getCampaignList
  } from "@/apis/campaignapis";
  import { useRoute } from "vue-router";

  export default {
    name: "AdminQRAnalytics",

    data() {
      return {
        qrStatusFilter: "",
        page_no:0,
        page_no1:0,
        showDeleteModal: false,
        deletingQr: false,
        qrToDelete: null,
        toast: null,
        ldata: null,
        browserChart: null,
        sourceChart: null,
        stypeChart: null,
        browserOptions: {
          chart: { type: "bar", height: 260 },
          plotOptions: {
            bar: { horizontal: true }
          },
          series: [{ name: "Users", data: [] }],
          xaxis: { categories: [] },
          colors: ["#6366f1"]
        },
        sourceOptions: {
          chart: { type: "bar", height: 260 },
          plotOptions: {
            bar: { horizontal: false }
          },
          series: [{ name: "Scans", data: [] }],
          xaxis: { categories: [] },
          colors: ["#f97316"]
        },
        stypeOptions: {
          chart: { type: "radialBar", height: 260 },
          series: [],
          labels: [],
          colors: ["#22c55e", "#eab308", "#ef4444"]
        },
        stats:{
          total_scans:0,
          unique_scans:0,
          today_scans:0,
          conversion_rate:"0.0"
        },
        qrForm: {
          title: "",
          parent: "",
          parent_type: "review",
          link: "",
          campaign: "",
          note: ""
        },

        creatingQr: false,
        showQRModal: false,

        qrList: [],
        scanLogs: [],

        selectedQrId: "",

        scanTrendChart: null,
        deviceChart: null,

        /* ================= SCAN TREND ================= */

        scanTrendOptions: {
          chart: {
            type: "area",
            height: 260,
            toolbar: { show: false }
          },

          stroke: {
            curve: "smooth",
            width: 3
          },

          colors: ["#197fe6"],

          series: [{
            name: "Scans",
            data: []
          }],

          xaxis: {
            categories: []
          },

          dataLabels: { enabled: false }
        },

        /* ================= DEVICE ================= */

        deviceOptions: {
          chart: { type: "donut" },

          labels: [],

          series: [],

          colors: ["#22c55e", "#197fe6", "#f59e0b"],

          legend: { position: "bottom" }
        },
        camps: [],
      };
    },

    async mounted() {
      const route = useRoute();
      const rtype = route.query.rtype;
      if (rtype && rtype.localeCompare("new") == 0) {
        this.showQRModal = true;
      }
      this.toast = useToast();
      const tmp = sessionStorage.getItem("ldata") || "";
      if (tmp && tmp.length > 0) {
        this.ldata = JSON.parse(tmp);
      }
      this.loadQrList();
      this.loadCampList();
      this.loadKpis();
      this.loadCharts();
      this.loadRecentScans();
    },
    watch: {
      "qrForm.parent_type"(newVal) {

        console.log("sdsa", newVal);
        this.loadCampList();
      }
    },
    methods: {
      async exportPDF() {

        const element = this.$refs.pdfContent;

        const canvas = await html2canvas(element, {
          scale: 2,          // higher quality
          useCORS: true
        });

        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let position = 0;

        if (imgHeight < pageHeight) {
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        } else {

          let heightLeft = imgHeight;

          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
        }

        pdf.save("customers_export.pdf");
      },
      async applyQrFilter() {
        this.page_no1 = 0; // reset pagination
        await this.loadQrList();
      },
      async changeQrPage(step) {

        if (step === -1 && this.page_no1 === 0) return;

        this.page_no1 += step;

        if (this.page_no1 < 0) this.page_no1 = 0;

        await this.loadQrList();
      },

      async changeScanPage(step) {

        if (step === -1 && this.page_no === 0) return;

        this.page_no += step;

        if (this.page_no < 0) this.page_no = 0;

        await this.loadRecentScans();
      },
    async downloadQr(qr) {

 var res = null;
        var url = "";
        if (qr.user_qr_parent_type === "review") {
          url = mainBaseUrl+"review/"+qr.cmp.camp_id;
          res = await getQrUrl(url, "camp", qr.cmp.camp_id, qr.cmp.camp_user);
        }
        else if (qr.user_qr_parent_type === "referral") {
          url = mainBaseUrl+"referral/"+qr.user_qr_camp;
          res = await getQrUrl(url, "ref", qr.user_qr_camp, qr.user_qr_user);
        }
        else{
           url = qr.user_qr_link;
          res = await getQrUrl(url, "custom", qr.user_qr_id, qr.user_qr_user);
        }

        if (!res.status) return;

  if (!res.status) return;

  const qrLink = apiBaseUrl + res.link;

  const qrCanvas = document.createElement("canvas");

  QRCode.toCanvas(qrCanvas, qrLink, {
    width: 600,
    margin: 1,
    errorCorrectionLevel:"H",
    color: {
      dark: "#197fe6",
      light: "#ffffff"
    }
  }, (error) => {

    if (error) {
      console.error("QR generation failed:", error);
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 1200;

    const ctx = canvas.getContext("2d");

    /* background */

    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    /* white card */

    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = "rgba(0,0,0,0.12)";
    ctx.shadowBlur = 30;
    ctx.fillRect(120, 120, 660, 920);
    ctx.shadowBlur = 0;

    /* header */

    ctx.fillStyle = "#197fe6";
    ctx.fillRect(120, 120, 660, 120);

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 42px Arial";
    ctx.textAlign = "center";
    ctx.fillText("REVIEWSFIX", 450, 195);

    /* QR */

    ctx.drawImage(qrCanvas, 240, 300, 420, 420);

    /* center logo */

    const logo = new Image();
    logo.src = "/images/logo.png";

    logo.onload = () => {

      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(450, 510, 70, 0, Math.PI * 2);
      ctx.fill();

      ctx.drawImage(logo, 400, 460, 100, 100);

      /* stars */

      ctx.fillStyle = "#fbbf24";
      ctx.font = "34px Arial";
      ctx.fillText("★★★★★", 450, 770);

      /* text */

      ctx.fillStyle = "#111";
      ctx.font = "bold 36px Arial";
      ctx.fillText("Scan to Leave a Review", 450, 840);

      ctx.fillStyle = "#64748b";
      ctx.font = "24px Arial";
      ctx.fillText("Your feedback helps us grow", 450, 890);

      ctx.fillStyle = "#197fe6";
      ctx.font = "bold 26px Arial";
      ctx.fillText("Powered by Reviewsfix", 450, 980);

      /* download */

      const downloadLink = document.createElement("a");
      downloadLink.download = `reviewsfix-${qr.user_qr_title || Date.now()}.png`;
      downloadLink.href = canvas.toDataURL("image/png");
      downloadLink.click();

      if (this.$toast) {
        this.$toast.success("Branded QR downloaded");
      }

    };

  });

},
      async copyQrLink(qr) {

        const link = qr.user_qr_link || qr.link;

        if (!link) return;

        await navigator.clipboard.writeText(link);

        // Optional toast instead of alert
        this.toast.success("QR link copied");
        console.log("QR link copied");
      },
      async shareQr(qr) {

        const link = qr.user_qr_link || qr.link;

        if (!link) return;

        if (navigator.share) {

          await navigator.share({
            title: qr.name,
            text: "Scan this QR Code",
            url: link
          });

        } else {

          await navigator.clipboard.writeText(link);
          console.log("Link copied (share fallback)");
        }
      },
      openDeleteModal(qr) {
        this.qrToDelete = qr;
        this.showDeleteModal = true;
      },
      async createQrHandler() {

        if (!this.qrForm.title.trim()) {
          this.toast.warning("QR Name required");
          return;
        }

        if (this.qrForm.parent_type !== "custom" && !this.qrForm.campaign.trim()) {
          this.toast.warning("Select Campaign");
          return;
        }

        this.creatingQr = true;


        const user_id = this.ldata.user_id;


        let link = this.qrForm.link;

        /* ✅ Destination Logic */

        if (this.qrForm.parent_type === "review") {
          link = `${window.location.origin}/r/${user_id}`;
        }

        if (this.qrForm.parent_type === "referral") {
          link = `${window.location.origin}/ref/${user_id}`;
        }

        const res = await createQr(
          user_id,
          this.qrForm.title,
          this.qrForm.parent,
          this.qrForm.parent_type,
          link,
          this.qrForm.campaign,
          this.qrForm.note
        );

        this.creatingQr = false;

        if (!res.status) {
          this.toast.error(res.success || "QR creation failed");
          return;
        }

        /* ✅ Reset Form */

        this.qrForm = {
          title: "",
          parent: "",
          parent_type: "review",
          link: "",
          campaign: "",
          note: ""
        };

        this.showQRModal = false;

        this.downloadQr(res.qr_dt);
        /* ✅ Reload QR List */

        await this.loadQrList();
      },
      newQrNow() {
        this.showQRModal = true;
      },
      /* ================= LOAD QR LIST ================= */

      async loadQrList() {

        const user_id = this.ldata.user_id;

        if (!user_id) return;

        const res = await getQrList(user_id,this.page_no1);

        if (!res.status) return;

        this.qrList = res.data;

        // if (this.qrList.length) {
        //   this.selectQr(this.qrList[0].id);
        // }
      },
      async loadCampList() {

        this.ldata = JSON.parse(sessionStorage.getItem("ldata"));
        const user_id = this.ldata.user_id;

        if (!user_id) return;
        if(this.qrForm.parent_type==='review'){
          const res = await getCampaignList(user_id, "", "", this.qrForm.parent_type + "", "", "none");
          if (!res.status) return;

          this.camps = res.campaigns;
        }
        else{
          const res = await getReferralCampaigns(user_id,"none","");
          if (!res.status) return;

          this.camps = res.records;
        }
      },

      /* ================= QR SELECTION ================= */

      async selectQr(qr_id) {

        this.selectedQrId = qr_id;

        await this.loadKpis();
        await this.loadCharts();
        await this.loadRecentScans();
      },

      /* ================= LOAD KPIs ================= */

      async loadKpis() {

        const res = await getQrKpiStats(this.selectedQrId);

        if (!res.status) return;

        this.stats = res.data;

        const qr = this.qrList.find(q => q.id === this.selectedQrId);

        if (qr) qr.scans = this.stats.total_scans;
      },

      /* ================= LOAD CHARTS ================= */

      async loadCharts() {

        const trendRes = await getScanTrend(this.selectedQrId);

        if (trendRes.status && trendRes.data.length) {

          this.scanTrendOptions.series[0].data =
            trendRes.data.map(d => parseInt(d.scans));

          this.scanTrendOptions.xaxis.categories =
            trendRes.data.map(d => d.dt);
        } else {

          this.scanTrendOptions.series[0].data = [];
          this.scanTrendOptions.xaxis.categories = [];
        }

        const deviceRes = await getDeviceBreakdown(this.selectedQrId);

        if (deviceRes.status && deviceRes.data.length) {

          this.deviceOptions.labels =
            deviceRes.data.map(d => d.device);

          this.deviceOptions.series =
            deviceRes.data.map(d => parseInt(d.total));
        } else {

          this.deviceOptions.labels = [];
          this.deviceOptions.series = [];
        }

        if (deviceRes.status && deviceRes.browsers.length) {
          this.browserOptions.series[0].data =
            deviceRes.browsers.map(b => parseInt(b.total));

          this.browserOptions.xaxis.categories =
            deviceRes.browsers.map(b => b.browser);

        } else {
          this.browserOptions.series[0].data = [];
          this.browserOptions.xaxis.categories = [];
        }

        if (deviceRes.status && deviceRes.sources.length) {
          this.sourceOptions.series[0].data =
            deviceRes.sources.map(s => parseInt(s.total));

          this.sourceOptions.xaxis.categories =
            deviceRes.sources.map(s => s.source);

        } else {
          this.sourceOptions.series[0].data = [];
          this.sourceOptions.xaxis.categories = [];
        }

        if (deviceRes.status && deviceRes.stypes.length) {

          this.stypeOptions.series =
            deviceRes.stypes.map(s => parseInt(s.total));

          this.stypeOptions.labels =
            deviceRes.stypes.map(s => s.stype || "Unknown");

        } else {
          this.stypeOptions.series = [];
          this.stypeOptions.labels = [];
        }

        this.renderCharts();
      },

      /* ================= LOAD RECENT SCANS ================= */

      async loadRecentScans() {

        const res = await getRecentScans(this.selectedQrId,this.page_no+"");

        if (!res.status) return;

        this.scanLogs = res.data.map(scan => ({
          id: scan.qr_scan_id,
          visitor: scan.qr_scan_browser || "Visitor",
          device: scan.qr_scan_device || "Unknown",
          source: scan.qr_scan_source || "Direct",
          location: scan.qr_scan_status || "Unknown",
          date: scan.qr_scan_datetime
        }));
      },

      /* ================= RENDER CHARTS (SAFE) ================= */

      renderCharts() {

        // destroy old charts safely
        if (this.scanTrendChart) this.scanTrendChart.destroy();
        if (this.deviceChart) this.deviceChart.destroy();
        if (this.browserChart) this.browserChart.destroy();
        if (this.sourceChart) this.sourceChart.destroy();
        if (this.stypeChart) this.stypeChart.destroy();

        // create charts
        this.scanTrendChart = new ApexCharts(
          document.querySelector("#scanTrendChart"),
          this.scanTrendOptions
        );

        this.deviceChart = new ApexCharts(
          document.querySelector("#deviceChart"),
          this.deviceOptions
        );

        this.browserChart = new ApexCharts(
          document.querySelector("#browserChart"),
          this.browserOptions
        );

        this.sourceChart = new ApexCharts(
          document.querySelector("#sourceChart"),
          this.sourceOptions
        );

        this.stypeChart = new ApexCharts(
          document.querySelector("#stypeChart"),
          this.stypeOptions
        );

        // render all
        this.scanTrendChart.render();
        this.deviceChart.render();
        this.browserChart.render();
        this.sourceChart.render();
        this.stypeChart.render();
      },

      /* ================= DELETE QR ================= */

      async confirmDeleteQr() {

        if (!this.qrToDelete) return;

        this.deletingQr = true;

        const user_id = localStorage.getItem("user_id");

        const res = await deleteQr(this.qrToDelete.user_qr_id, user_id);

        this.deletingQr = false;

        if (!res.status) return;

        this.showDeleteModal = false;

        this.loadQrList();
        this.loadKpis();
      }
    }
  };
</script>
<style scoped>
  /* ================= PAGE ================= */

  .qr-analytics-page {
    padding: 28px;
    background: #f6f7f8;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-width: 0;
  }

  /* ================= HEADER ================= */

  .page-header {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }

  .page-header h1 {
    font-size: 28px;
    font-weight: 800;
  }

  .page-header p {
    font-size: 14px;
    color: #64748b;
  }

  /* ================= KPI ================= */

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
  }

  .kpi-card {
    background: white;
    border-radius: 16px;
    padding: 16px;
    border: 1px solid #e2e8f0;
  }

  .kpi-card span {
    font-size: 12px;
    color: #64748b;
  }

  .kpi-card strong {
    font-size: 22px;
    display: block;
    margin-top: 6px;
  }

  /* ================= GRID ================= */

  .charts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 14px;
  }

  .chart-card {
    background: white;
    border-radius: 18px;
    padding: 18px;
    border: 1px solid #e2e8f0;
    min-width: 0;
  }

  .chart-card.large {
    grid-column: span 2;
  }

  /* ================= CHART FIX ================= */

  .chart-wrapper {
    width: 100%;
    height: 260px;
    overflow: hidden;
  }

  .chart-wrapper ::v-deep(.apexcharts-canvas) {
    max-width: 100% !important;
  }

  /* ================= TABLE ================= */

  .table-card {
    background: white;
    border-radius: 18px;
    border: 1px solid #e2e8f0;
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    color: #94a3b8;
    padding: 14px;
  }

  td {
    padding: 14px;
    border-top: 1px solid #f1f5f9;
  }

  .muted {
    color: #94a3b8;
    font-size: 12px;
  }

  .positive {
    color: #16a34a;
  }

  .neutral {
    color: #94a3b8;
  }

  /* ================= MOBILE ================= */

  @media(max-width:768px) {

    .chart-card.large {
      grid-column: span 1;
    }

    .qr-analytics-page {
      padding: 16px;
    }
  }

  /* ================= PAGE ================= */

  .qr-analytics-page {
    padding: 28px;
    background: #f6f7f8;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-width: 0;
    /* ✅ prevents flex overflow */
  }

  /* ================= HEADER ================= */

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
  }

  .page-header h1 {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
  }

  .page-header p {
    font-size: 14px;
    color: #64748b;
    margin-top: 4px;
  }

  /* ================= HEADER ACTIONS ================= */

  .header-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }

  /* ================= BUTTON SYSTEM ================= */

  .filter-btn {
    height: 40px;
    padding: 0 14px;
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: 0.15s;
    white-space: nowrap;
  }

  .filter-btn:hover {
    background: #f8fafc;
  }

  .primary-btn {
    height: 40px;
    padding: 0 16px;
    background: #197fe6;
    border-radius: 12px;
    color: white;
    font-size: 13px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: 0.15s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  .primary-btn:hover {
    background: #166fd1;
  }

  .ghost-btn {
    height: 40px;
    padding: 0 14px;
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: 0.15s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  .ghost-btn:hover {
    background: #f8fafc;
  }

  .ghost-btn span {
    font-size: 18px;
    color: #64748b;
  }

  /* ================= KPI STRIP ================= */

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
  }

  .kpi-card {
    background: white;
    border-radius: 16px;
    padding: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .kpi-card span {
    font-size: 12px;
    color: #64748b;
  }

  .kpi-card strong {
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
  }

  .kpi-card small {
    font-size: 11px;
  }

  /* ================= GRID ================= */

  .charts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 14px;
  }

  .chart-card {
    background: white;
    border-radius: 18px;
    padding: 18px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
    min-width: 0;
    /* ✅ critical */
    position: relative;
  }

  .chart-card.large {
    grid-column: span 2;
  }

  /* ================= CARD HEADER ================= */

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
  }

  .card-header h3 {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
  }

  /* ================= CHART WRAPPER ================= */

  .chart-wrapper {
    width: 100%;
    height: 260px;
    overflow: hidden;
    /* ✅ PREVENT BLEED */
    position: relative;
  }

  /* ✅ ApexCharts Overflow Kill */

  .chart-wrapper ::v-deep(.apexcharts-canvas) {
    max-width: 100% !important;
  }

  .chart-wrapper ::v-deep(svg) {
    max-width: 100% !important;
  }

  /* ================= TABLE CARD ================= */

  .table-card {
    background: white;
    border-radius: 18px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
    overflow-x: auto;
  }

  /* ================= TABLE ================= */

  table {
    width: 100%;
    border-collapse: collapse;
  }

  thead {
    background: #f8fafc;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    font-weight: 700;
    color: #94a3b8;
    padding: 14px 18px;
    text-align: left;
  }

  td {
    padding: 14px 18px;
    border-top: 1px solid #f1f5f9;
    font-size: 13px;
    color: #0f172a;
  }

  tbody tr:hover {
    background: #f8fafc;
  }

  /* ================= TEXT HELPERS ================= */

  .muted {
    color: #94a3b8;
    font-size: 12px;
  }

  .positive {
    color: #16a34a;
  }

  .warning {
    color: #f59e0b;
  }

  .neutral {
    color: #94a3b8;
  }

  .danger {
    color: #ef4444;
  }

  /* ================= MOBILE ================= */

  @media (max-width: 1024px) {

    .chart-card.large {
      grid-column: span 1;
      /* ✅ FIX */
    }

    .chart-wrapper {
      height: 240px;
    }
  }

  @media (max-width: 768px) {

    .qr-analytics-page {
      padding: 16px;
    }

    .page-header {
      flex-direction: column;
    }

    .header-actions {
      width: 100%;
    }

    .filter-btn,
    .ghost-btn,
    .primary-btn {
      flex: 1;
      justify-content: center;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .charts-grid {
      grid-template-columns: 1fr;
    }

    table {
      min-width: 700px;
    }
  }

  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 22px 10px;
    border-bottom: 1px solid #f1f5f9;
  }

  .table-header h3 {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
  }

  .table-header p {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 2px;
  }

  .table-card {
    background: white;
    border-radius: 18px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
    overflow-x: auto;
    padding-bottom: 6px;
  }

  .table-action-btn {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: 0.15s;
  }

  .table-action-btn span {
    font-size: 18px;
    color: #64748b;
  }

  .table-action-btn:hover {
    background: #f1f5f9;
  }

  .table-action-btn:hover span {
    color: #197fe6;
  }

  .ghost-btn.small {
    height: 30px;
    padding: 0 10px;
    font-size: 12px;
    border-radius: 8px;
  }

  .modal-card {
    width: 420px;
    background: white;
    border-radius: 18px;
    box-shadow: 0 40px 90px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 20px;
    border-bottom: 1px solid #f1f5f9;
  }

  .modal-header h3 {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }

  .modal-close {
    border: none;
    background: none;
    font-size: 16px;
    cursor: pointer;
    color: #94a3b8;
  }

  .modal-close:hover {
    color: #ef4444;
  }

  .modal-body {
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-group label {
    font-size: 12px;
    font-weight: 600;
    color: #334155;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    height: 36px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 0 10px;
    font-size: 13px;
    background: white;
  }

  .form-group textarea {
    height: 70px;
    padding-top: 8px;
    resize: none;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f1f5f9;
    background: #f8fafc;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  .popup-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
    animation: fadeIn 0.15s ease;
  }

  .popup-modal {
    width: 420px;
    background: white;
    border-radius: 18px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    animation: modalEnter 0.18s ease;
  }

  .popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 20px;
    border-bottom: 1px solid #f1f5f9;
  }

  .popup-header h3 {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }

  .popup-close {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: 0.15s;
  }

  .popup-close span {
    font-size: 18px;
    color: #64748b;
  }

  .popup-close:hover {
    background: #f8fafc;
  }

  .popup-close:hover span {
    color: #ef4444;
  }

  .popup-body {
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-group label {
    font-size: 12px;
    font-weight: 600;
    color: #334155;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    height: 36px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 0 10px;
    font-size: 13px;
    background: white;
    transition: 0.15s;
  }

  .form-group textarea {
    height: 70px;
    padding-top: 8px;
    resize: none;
  }

  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #197fe6;
    box-shadow: 0 0 0 3px rgba(25, 127, 230, 0.12);
  }

  .popup-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f1f5f9;
    background: #f8fafc;
  }

  @keyframes modalEnter {
    from {
      transform: translateY(8px) scale(0.98);
      opacity: 0;
    }

    to {
      transform: none;
      opacity: 1;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  .bottom-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  @media (max-width: 1024px) {
    .bottom-grid {
      grid-template-columns: 1fr;
    }
  }

  .popup-modal.confirm {
    width: 380px;
  }

  .confirm-text {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
  }

  .danger-btn {
    height: 36px;
    padding: 0 14px;
    border-radius: 10px;
    border: none;
    background: #ef4444;
    color: white;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  .danger-btn:hover {
    background: #dc2626;
  }

  .actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
  }

  .table-action-btn.danger span {
    color: #ef4444;
  }

  .table-action-btn:hover.danger span {
    color: #dc2626;
  }
  .pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid #f1f5f9;
}

.page-btn {
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.15s;
}

.page-btn:hover:not(:disabled) {
  background: #197fe6;
  color: white;
  border-color: #197fe6;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}
table {
  table-layout: fixed;
}

th, td {
  word-wrap: break-word;
  white-space: normal;
}
.qr-cell strong {
  display: block;
  word-break: break-word;
}

td strong {
  word-break: break-word;
}
.table-card {
  overflow: hidden;
}
.actions {
  width: 120px;
  flex-wrap: wrap;
}

.table-action-btn {
  flex-shrink: 0;
}
@media (max-width: 768px) {

  th, td {
    font-size: 12px;
    padding: 10px;
  }

  .actions {
    justify-content: flex-start;
  }

}
.qr-filter select {
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  background: white;
  cursor: pointer;
  transition: 0.15s;
}

.qr-filter select:focus {
  outline: none;
  border-color: #197fe6;
  box-shadow: 0 0 0 3px rgba(25,127,230,0.12);
}
</style>